<template>

    <v-app-bar tile app clipped-left v-if="visible" color="grey darken-4">
        <v-app-bar-nav-icon @click.stop="cycleDrawerMenu"></v-app-bar-nav-icon>

        <img class="mr-5" :src="cdnBaseUrl('/img/sample_ninja_app_logo.png')"/>

        <div class="d-flex title grey--text">

            <div class="align-self-center">
                {{ settings.name }}
            </div>

            <!-- SIMULATION MODE LABEL -->
            <v-scale-transition>
                <v-tooltip bottom color="primary" max-width="400" v-show="settings.demo_mode===true">
                    <template v-slot:activator="{ on }">
                        <v-chip v-on="on"
                                v-show="settings.demo_mode===true && !updateAvailable"
                                color="#323232"
                                class="ml-3 mr-2 align-self-center grey--text"
                                label
                                small>
                            SIMULATION
                        </v-chip>
                    </template>
                    <div class="mt-2 mb-2">

                        <v-alert type="info" prominent>Simulation mode</v-alert>

                        Your are in the simulation mode. In this mode you can run projects like you would in the real
                        world and get a good feel for how the system works.
                        Sample Ninja will play your panelists and automatically respond to any invitations.
                        <br><br>
                        If you would like to experience the panelist side of things simply register a panelist
                        with your email address. In the simulation mode Sample Ninja will send out real emails
                        to all the application users while playing the rest of the panelists.
                    </div>
                </v-tooltip>
            </v-scale-transition>

            <!-- UPDATE AVAILABLE LABEL -->
            <v-scale-transition>
                <v-tooltip bottom color="primary" max-width="200">
                    <template v-slot:activator="{ on }">
                        <v-chip v-show="updateAvailable"
                                v-on="on"
                                small
                                label
                                class="ml-3 mr-2 align-self-center"
                                @click="updateButtonClicked"
                                color="red darken-2">
                            UPDATE
                        </v-chip>
                    </template>
                    Application update is available. Click here to update or simply reload the page.
                </v-tooltip>
            </v-scale-transition>

        </div>

        <v-spacer></v-spacer>

        <v-tooltip bottom
                   width="400"
                   max-width="400"
                   open-delay="1000"
                   color="primary"
                   v-for="user in getChatUsers"
                   :key="user.id">

            <template v-slot:activator="{ on: tooltip }">

                <v-menu
                    v-model="user.chatModel"
                    :close-on-content-click="false"
                    offset-y
                    min-width="400"
                    max-width="400"
                    max-height="600"
                    nudge-bottom="10"
                >
                    <template v-slot:activator="{ on : menu }">

                        <div v-on="{...menu, ...tooltip}" class="pr-1 cursor-pointer">

                            <v-badge overlap
                                     color="red"
                                     :value="privateMessageCount(user) && !user.chatModel"
                                     :content="privateMessageCount(user) > 0 ? privateMessageCount(user) : null">

                                <avatar v-if="user.photo_url"
                                        :person-name="user.name"
                                        :disable-tooltip="true"
                                        :image-url="user.photo_url"/>

                                <avatar v-else
                                        :person-name="user.name"
                                        :disable-tooltip="true"
                                        color="primary"/>
                            </v-badge>
                        </div>
                    </template>

                    <private-chat :user="user" :visible="user.chatModel">
                    </private-chat>

                </v-menu>

            </template>
            <div class="d-flex pt-2 pb-2">
                <div class="pr-3">
                    <avatar :size="70" :image-url="user.photo_url" :person-name="user.name"></avatar>
                </div>
                <div class="flex-grow-1 align-self-center">
                    <div class="title">{{ user.name }}</div>
                    <div class="body-2">is currently online</div>
                </div>
            </div>
        </v-tooltip>

        <v-spacer></v-spacer>

        <socket-indicator></socket-indicator>

        <!-- FULLSCREEN -->
        <v-btn icon @click="fullscreenClicked" v-if="!fullscreen">
            <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on }">
                    <v-icon v-on="on" medium>mdi-fullscreen</v-icon>
                </template>
                <span>Go fullscreen</span>
            </v-tooltip>
        </v-btn>

        <!-- EXIT FULL SCREEN -->
        <v-btn icon @click="fullscreenClicked" v-if="fullscreen">
            <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on }">
                    <v-icon v-on="on" medium>mdi-fullscreen-exit</v-icon>
                </template>
                <span>Exit fullscreen</span>
            </v-tooltip>
        </v-btn>

        <!-- PRODUCT BOARD ICON -->
        <v-btn icon @click="showProductBoard">
            <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on }">
                    <v-icon v-on="on" medium>mdi-account-voice</v-icon>
                </template>
                <span>Share your feedback and product ideas</span>
            </v-tooltip>
        </v-btn>

        <!-- CHAT ICON -->
        <v-btn icon @click="showChat">
            <v-badge overlap
                     color="red"
                     :value="teamMessageCount"
                     :content="teamMessageCount > 0 ? teamMessageCount : null">
                <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on }">
                        <v-icon v-on="on" medium>mdi-message-outline</v-icon>
                    </template>
                    <span>Chat with your team members real time</span>
                </v-tooltip>
            </v-badge>
        </v-btn>

        <!-- SHOW ANNOUNCEMENTS ICON -->
        <v-btn icon @click="showAnnouncements">
            <v-badge overlap
                     color="red"
                     :value="notificationsCount"
                     :content="notificationsCount > 0 ? notificationsCount : null">
                <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on }">
                        <v-icon medium v-on="on">mdi-bell-outline</v-icon>
                    </template>
                    <span>View notifications</span>
                </v-tooltip>
            </v-badge>
        </v-btn>

        <!-- AVATAR USER MENU -->
        <div class="cursor-pointer">
            <v-menu v-model="userMenuModel" class="ml-4 mr-4" bottom offset-y nudge-bottom="10">

                <template v-slot:activator="{ on }">
                    <div v-on="on" class="ml-4">
                        <avatar v-on="on"
                                @click="userMenuModel=true"
                                large
                                :image-url="user.photo_url"
                                :person-name="user.name"
                                tooltip-direction="left"
                        >
                            My account menu
                        </avatar>
                    </div>
                </template>

                <v-list dense class="pt-0">

                    <v-list-item @click="$router.push('/spa/account')">
                        <v-list-item-action>
                            <v-icon>mdi-cog</v-icon>
                        </v-list-item-action>
                        <v-list-item-subtitle>My account</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="hasRole('Super Admin')" @click="openHorizon">
                        <v-list-item-action>
                            <v-icon>mdi-server-network</v-icon>
                        </v-list-item-action>
                        <v-list-item-subtitle>Background jobs</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="hasRole('Super Admin')" @click="$router.push('/spa/tenantmanager')">
                        <v-list-item-action>
                            <v-icon>mdi-office-building</v-icon>
                        </v-list-item-action>
                        <v-list-item-subtitle>Tenant manager</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="hasRole('Super Admin')" @click="$router.push('/spa/announcements')">
                        <v-list-item-action>
                            <v-icon>mdi-message-outline</v-icon>
                        </v-list-item-action>
                        <v-list-item-subtitle>Announcements</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="hasRole('Super Admin')" @click="$router.push('/spa/testground')">
                        <v-list-item-action>
                            <v-icon>mdi-seat-recline-extra</v-icon>
                        </v-list-item-action>
                        <v-list-item-subtitle>Test ground</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item @click.prevent="showSupportOptions">
                        <v-list-item-action>
                            <v-icon>mdi-help-circle</v-icon>
                        </v-list-item-action>
                        <v-list-item-subtitle>Support</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item @click="sendToUrl('/logout')">
                        <v-list-item-action>
                            <v-icon>mdi-exit-run</v-icon>
                        </v-list-item-action>
                        <v-list-item-subtitle>Logout</v-list-item-subtitle>
                    </v-list-item>

                </v-list>

            </v-menu>
        </div>

        <!-- PRODUCT BOARD DIALOG -->
        <product-board-dialog v-model="productBoardVisible"></product-board-dialog>

        <!-- SUPPORT DIALOG -->
        <v-dialog v-model="supportDialogOpen"
                  max-width="600px"
                  persistent>

            <toolbar-page title="Support options">
                <template slot="buttons">
                    <v-btn @click="hideSupportOptions" icon>
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </template>
            </toolbar-page>

            <v-card>
                <v-img :src="cdnBaseUrl('/img/support.png')"></v-img>

                <v-card-text>
                    <v-alert class="mt-5 mb-0 body-2"
                             type="info"
                             icon="mdi-email">
                        support@sampleninja.io
                    </v-alert>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- APPLICATION UPDATED DIALOG -->
        <application-updated-dialog v-model="applicationUpdatedDialogModel"
                                    :build="applicationBuild"
                                    :key="'build_' + applicationBuild"
        ></application-updated-dialog>

    </v-app-bar>

</template>
<script>

import Avatar from '../UI/Avatar';
import {mapGetters, mapMutations, mapState} from 'vuex';
import ProductBoardDialog from "../UI/ProductBoardDialog";
import ToolbarPage from "../UI/ToolbarPage";
import SocketIndicator from './SockerIndicator';
import PrivateChat from "../../modules/Chat/components/PrivateChat";
import {timeDisplay} from "../mixins/timeDisplayMixin";
import ApplicationUpdatedDialog from "./ApplicationUpdatedDialog";

export default {

    mixins: [timeDisplay],

    components: {
        Avatar,
        ProductBoardDialog,
        ToolbarPage,
        SocketIndicator,
        PrivateChat,
        ApplicationUpdatedDialog
    },

    props: {
        updateAvailable: {
            type: Boolean,
            required: true,
        },
        applicationBuild: {
            type: Number,
            required: true,
        }
    },

    data() {
        return {
            drawer: true,
            visible: true,
            burgerCycle: ['wide', 'narrow', 'hidden'],
            burgerCycleIndex: 0,
            notificationCount: 0,
            supportDialogOpen: false,
            userMenuModel: false,
            productBoardVisible: false,
            fullscreen: false,
            applicationUpdatedDialogModel: false,

        }
    },

    created() {
        Bus.$on('chat-message-count', (count) => {
            this.chatMessageCount = count;
        });

        Bus.$on('toolbar', (value) => {
            if (typeof value == 'undefined') {
                this.visible = !this.visible;
            } else {
                this.visible = value;
            }
        });

        document.addEventListener('fullscreenchange', () => {
            if (document.fullscreenElement) {
                this.fullscreen = true;
            } else {
                this.fullscreen = false;
            }
        });
    },

    methods: {

        ...mapMutations('Chat', [
            'markTeamMessagesSeen'
        ]),

        updateButtonClicked() {
            this.applicationUpdatedDialogModel = true;
        },

        showChat() {
            Bus.$emit('chat-drawer-toggle');
            this.markTeamMessagesSeen();
        },

        showAnnouncements() {
            Bus.$emit('announcements-drawer-toggle');
        },

        cycleDrawerMenu() {

            this.burgerCycleIndex++;

            if (this.burgerCycleIndex === 3) this.burgerCycleIndex = 0;

            Bus.$emit('navigation-drawer-cycle', this.burgerCycle[this.burgerCycleIndex]);
        },

        showSupportOptions() {
            this.supportDialogOpen = true;
        },

        hideSupportOptions() {
            this.supportDialogOpen = false;
        },

        showProductBoard() {
            this.productBoardVisible = true;
        },

        openHorizon() {
            window.open('/horizon', '_blank');
        },

        fullscreenClicked() {

            if (!document.fullscreenEnabled) {
                Bus.$emit('flash-message', {
                    type: 'error',
                    message: 'Your browser does not support full screen mode'
                });
            } else {

                if (!document.fullscreenElement) {
                    document.getElementById('html').requestFullscreen();
                } else {
                    document.exitFullscreen();
                }
            }
        },

        privateMessageCount(user) {
            if (this.$store.state.Chat) {

                const key = "user_" + user.id;

                if (!this.privateMessages.hasOwnProperty(key)) {
                    return 0;
                }

                let messagesByOthers = this.privateMessages[key].filter((item) => {
                    return item.user.id !== this.$store.state.user.id && item.seen === false
                });

                return messagesByOthers.length;
            } else {
                return 0;
            }
        }
    },

    computed: {

        ...mapState(['user']),

        ...mapState('Chat', [
            'teamMessages',
            'privateMessages'
        ]),

        ...mapState('PanelSettings', [
            'settings',
        ]),

        ...mapGetters('Chat', [
            'getChatUsers',
        ]),

        appName() {
            if (this.settings.name) {
                return this.settings.name;
            } else {
                return 'Sample Ninja';
            }
        },

        notificationsCount() {
            return this.$store.state.announcements.length +
                this.$store.state.notifications.length;
        },

        teamMessageCount() {
            if (this.$store.state.Chat) {
                let messagesByOthers = this.teamMessages.filter((item) => {
                    return item.user.id !== this.$store.state.user.id && item.seen === false
                });
                return messagesByOthers.length;
            } else {
                return 0;
            }
        },

        onTrial() {
            return !!this.$store.state.PanelSettings.settings.trial_expires_on;
        },

        trialMessage() {
            return 'Your trial ends ' + this.toHumanTill(this.$store.state.PanelSettings.settings.trial_expires_on);
        }
    },
}
</script>